import { Alert, List, Stack, Table, Text } from '@mantine/core'
import { IconInfoCircle, IconPencil } from '@tabler/icons-react'
import { MetaList, SectionCard } from '@lighthouse/design'
import { useReportDetailOperations } from '../../hooks/useReportDetail'
import { NOT_MEASURED, exactNumber, whenLabel } from '../../utils/format'
import type { ReportDetailWindow as WindowDays } from '../../utils/reportDetailPath'
import type {
  ReportDetailBackgroundExports, ReportDetailDay, ReportDetailDurations, ReportDetailFailures, ReportDetailOperations,
  ReportDetailOutcomeWindow, ReportDetailSource, ReportDetailSourceFailures, ReportDetailWindow,
} from '../../types/reportDetail'
import { Caption, EvaluatedAt, Figure, TabFailure, TabLoading, WindowBanner } from './OverviewTab'
import {
  BACKGROUND_EXPORTS_ONLY,
  backgroundExportsText,
  dayFailedText,
  durationMsText,
  durationsSinceText,
  exportErrorLabel,
  exportRequesterText,
  failedOfView,
  failureRateView,
  interactiveView,
  outcomesState,
  outcomesText,
  outcomesTitle,
  recentFailuresCapText,
  recordedAsLabel,
  rowsBeforeText,
  sampleText,
  sourceCountView,
  windowTitle,
} from './reportDetailFormat'

/**
 * Report Detail → Operations: what the run log recorded for this report, by
 * source, in a window bounded by what retention keeps.
 *
 * TWO MEANINGS OF A ROW, ONE DATE BETWEEN THEM (decision R2, 2026-10-01). The
 * log's older rows are grid pages and delivery attempts, all written as
 * successes; newer ones are one per open, delivery and export, with their
 * outcome. So:
 *  - Row counts span both, and each source says so beside its count.
 *  - Failures, rates and interactive timings are counted only inside the
 *    OUTCOME window the server serves (`outcomes.from` onwards). While it has
 *    none — the cutover date is set by hand and ships unset — every one of them
 *    is NOT_MEASURED with the server's reason, never 0 and never 0%.
 *  - A rate is shown only from 20 recorded runs of ONE source, as the server
 *    computed it; below that, "F of N failed" with the reason. Never pooled.
 *  - Export durations: two writers time different spans; a dash, with the reason.
 *  - "Last N days" over a log that does not reach back that far: the figures
 *    are retitled "Since <date>".
 */
export function OperationsTab({ reportId, days }: { reportId: string; days: WindowDays }) {
  const { data, isLoading, isError, error } = useReportDetailOperations(reportId, days, { open: true })

  if (isError) return <TabFailure error={error} what="this report’s operations" />
  if (isLoading || !data) return <TabLoading />

  return <OperationsBody operations={data} />
}

/**
 * Where outcomes are counted inside the window, in the server's words: the
 * caption when there is an outcome window, the reason when there is none.
 */
export function OutcomesBanner({ outcomes, window }: { outcomes: ReportDetailOutcomeWindow; window: ReportDetailWindow }) {
  const state = outcomesState(outcomes)
  const title = state === 'unavailable'
    ? 'Failures, opens and deliveries are not counted yet'
    : `Outcomes counted — ${outcomesTitle(outcomes, window)}`
  return (
    <Alert
      variant="light"
      color={state === 'whole' ? 'gray' : 'yellow'}
      icon={<IconInfoCircle size={16} />}
      title={title}
      data-outcomes={state}
    >
      <Text size="sm">{outcomesText(outcomes)}</Text>
    </Alert>
  )
}

function OperationsBody({ operations }: { operations: ReportDetailOperations }) {
  const heading = windowTitle(operations.window)
  const outcomesHeading = outcomesTitle(operations.outcomes, operations.window)

  return (
    <Stack gap="lg">
      <WindowBanner window={operations.window} />
      <OutcomesBanner outcomes={operations.outcomes} window={operations.window} />

      {operations.definitionChangedInWindow && (
        <Alert variant="light" color="yellow" icon={<IconPencil size={16} />} title="Edited inside this window">
          <Text size="sm">
            Modified {whenLabel(operations.modifiedAt)}. {operations.definitionChangeCaption}
          </Text>
        </Alert>
      )}

      <SectionCard title={`Recorded by source — ${heading}`} subtitle="Three sources, never pooled: each row counts a different thing">
        <Stack gap="sm">
          <Caption>{operations.interactiveRecordableReason}</Caption>
          <SourceTable operations={operations} />
        </Stack>
      </SectionCard>

      <FailuresCard
        failures={operations.failures}
        title={operations.outcomes.from == null ? 'Failures' : `Failures — ${outcomesHeading}`}
      />

      <BackgroundExportsCard exports={operations.backgroundExports} />

      <SectionCard title={`Daily — ${heading}`} subtitle="UTC days with at least one recorded row, newest first">
        <DailyTable daily={operations.daily} recordable={operations.interactiveRecordable} caption={operations.dailyCaption} />
      </SectionCard>

      <SectionCard title="Notes">
        <List size="sm" spacing={4}>
          {operations.notes.map((note) => <List.Item key={note}>{note}</List.Item>)}
        </List>
      </SectionCard>

      <EvaluatedAt at={operations.evaluatedAt} />
    </Stack>
  )
}

function SourceTable({ operations }: { operations: ReportDetailOperations }) {
  return (
    <Table.ScrollContainer minWidth={760}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Source</Table.Th>
            <Table.Th>Rows recorded</Table.Th>
            <Table.Th>Durations</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {operations.bySource.map((source) => (
            <Table.Tr key={source.source} data-source={source.source}>
              <Table.Td>
                <Text size="sm" fw={500}>{source.source}</Text>
                <Text size="xs" c="dimmed">{recordedAsLabel(source.recordedAs)}</Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={2}>
                  <SourceCount source={source} recordable={operations.interactiveRecordable} />
                  {rowsBeforeText(source, operations.outcomes) && (
                    <Text size="xs">{rowsBeforeText(source, operations.outcomes)}</Text>
                  )}
                  <Caption>{source.unitCaption}</Caption>
                </Stack>
              </Table.Td>
              <Table.Td>
                {source.durations
                  ? <Durations durations={source.durations} since={durationsSinceText(source.source, operations.outcomes)} />
                  : <Figure value={NOT_MEASURED} reason={source.durationsUnavailableReason} />}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

function SourceCount({ source, recordable }: { source: ReportDetailSource; recordable: boolean }) {
  const view = source.source === 'Interactive'
    ? interactiveView(source.rowCount, recordable, source.rowCountUnavailableReason)
    : sourceCountView(source)
  return <Figure value={view.value} reason={view.reason} />
}

/**
 * p50, p90 and max, n always beside them; a dash below a percentile's minimum. `since` says where the sample starts
 * when that is not the card's own window — the interactive timings are read only from the cutover.
 */
function Durations({ durations, since }: { durations: ReportDetailDurations; since: string | null }) {
  return (
    <Stack gap={2}>
      <MetaList
        items={[
          { label: 'p50', value: <Figure value={durationMsText(durations.p50Ms)} reason={durations.p50UnavailableReason} /> },
          { label: 'p90', value: <Figure value={durationMsText(durations.p90Ms)} reason={durations.p90UnavailableReason} /> },
          { label: 'max', value: <Figure value={durationMsText(durations.maxMs)} reason={durations.maxUnavailableReason} /> },
        ]}
      />
      <Text size="xs" fw={500} data-durations-sample>{sampleText(durations, since)}</Text>
      <Caption>Measures {durations.measures}. There is no mean, by decision.</Caption>
    </Stack>
  )
}

function FailuresCard({ failures, title }: { failures: ReportDetailFailures; title: string }) {
  const outcomes = failures.scheduleLastOutcomes
  return (
    <SectionCard
      title={title}
      subtitle={`By source, never pooled; a rate only from ${exactNumber(failures.minimumRunsForRate)} recorded runs of one source`}
    >
      <Stack gap="sm">
        {failures.runLogRecordsFailuresReason && <Caption>{failures.runLogRecordsFailuresReason}</Caption>}
        <Table.ScrollContainer minWidth={640}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Source</Table.Th>
                <Table.Th>Failed of recorded</Table.Th>
                <Table.Th>Failure rate</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[failures.interactive, failures.scheduled, failures.export].map((source) => (
                <SourceFailuresRow key={source.source} failures={source} />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Text size="sm" fw={500} mt="xs">Each schedule’s last outcome</Text>
        <MetaList
          items={[
            { label: 'Schedules', value: exactNumber(outcomes.schedules) },
            { label: 'Last run failed', value: exactNumber(outcomes.lastFailed) },
            { label: 'Last run delivered', value: exactNumber(outcomes.lastDelivered) },
            { label: 'Never run', value: exactNumber(outcomes.neverRun) },
            { label: 'Unrecognised status', value: exactNumber(outcomes.unrecognised) },
          ]}
        />
        <Caption>{outcomes.caption}</Caption>
      </Stack>
    </SectionCard>
  )
}

function SourceFailuresRow({ failures }: { failures: ReportDetailSourceFailures }) {
  const counts = failedOfView(failures)
  const rate = failureRateView(failures)
  return (
    <Table.Tr data-failures={failures.source}>
      <Table.Td>
        <Text size="sm" fw={500}>{failures.source}</Text>
        <Caption>{failures.caption}</Caption>
      </Table.Td>
      <Table.Td><Figure value={counts.value} reason={counts.reason} /></Table.Td>
      <Table.Td>
        {/* The reason beside the counts already says why; beside the rate it is said only when it differs. */}
        <Figure value={rate.value} reason={rate.reason === counts.reason ? null : rate.reason} />
      </Table.Td>
    </Table.Tr>
  )
}

function BackgroundExportsCard({ exports }: { exports: ReportDetailBackgroundExports }) {
  return (
    <SectionCard
      title={`Background exports — since ${whenLabel(exports.windowStart)}`}
      subtitle="From the export table, which is trimmed only beyond 90 days (the widest window offered), so it covers the whole requested window"
    >
      <Stack gap="sm">
        <Figure value={backgroundExportsText(exports)} reason={BACKGROUND_EXPORTS_ONLY} />
        <MetaList
          items={[
            { label: 'Completed', value: exactNumber(exports.completedCount) },
            { label: 'In flight (queued or running)', value: exactNumber(exports.inFlightCount) },
            { label: 'Completed exactly at the row cap (possibly truncated)', value: exactNumber(exports.possiblyTruncatedCount) },
          ]}
        />
        <Caption>{exports.caption}</Caption>
        {exports.recentFailures.length > 0 && recentFailuresCapText(exports) && (
          <Caption>{recentFailuresCapText(exports)}.</Caption>
        )}
        {exports.recentFailures.length > 0 && exports.requesterWithheldCaption && (
          <Caption>{exports.requesterWithheldCaption}</Caption>
        )}
        {exports.recentFailures.length > 0 && (
          <Table.ScrollContainer minWidth={560}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Requested</Table.Th>
                  <Table.Th>By</Table.Th>
                  <Table.Th>Why it failed (category)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {exports.recentFailures.map((failure, index) => (
                  <Table.Tr key={`${failure.requestedAt}-${index}`}>
                    <Table.Td>{whenLabel(failure.requestedAt)}</Table.Td>
                    <Table.Td>{exportRequesterText(failure)}</Table.Td>
                    <Table.Td>{exportErrorLabel(failure.errorCategory)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </SectionCard>
  )
}

function DailyTable({ daily, recordable, caption }: { daily: ReportDetailDay[] | null; recordable: boolean; caption: string }) {
  if (daily == null) return <Figure value={NOT_MEASURED} reason="the run log is empty" />

  const active = daily.filter((d) => (d.interactive ?? 0) + d.scheduled + d.exports > 0).reverse()
  if (active.length === 0) {
    return <Text size="sm" c="dimmed">No row of this report is recorded on any of the {exactNumber(daily.length)} days in the series.</Text>
  }

  return (
    <Stack gap="xs">
      <Caption>
        {exactNumber(active.length)} of {exactNumber(daily.length)} days in the series have a recorded row; days with
        none are left out. {caption}
      </Caption>
      <Table.ScrollContainer minWidth={600}>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Day (UTC)</Table.Th>
              <Table.Th>Interactive rows</Table.Th>
              <Table.Th>Scheduled rows</Table.Th>
              <Table.Th>Exports</Table.Th>
              <Table.Th>Failed (interactive · scheduled · export)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {active.map((day) => (
              <Table.Tr key={day.day} data-day={day.day}>
                <Table.Td>{day.day}</Table.Td>
                <Table.Td>{interactiveView(day.interactive, recordable, null).value}</Table.Td>
                <Table.Td>{exactNumber(day.scheduled)}</Table.Td>
                <Table.Td>{exactNumber(day.exports)}</Table.Td>
                <Table.Td>{dayFailedText(day)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  )
}
